import React, { useState, useRef, useEffect } from "react";
import { ScheduledClass } from "./IClassContextBar";
import { ChevronDown, Calendar, MapPin, Layers, Book } from "lucide-react";
import "./ClassContext.scss";

interface ClassContextBarProps {
  selectedClass?: ScheduledClass | "custom" | null;
  classes?: any;
  onSelectClass?: (cls: ScheduledClass | "custom") => void;
  dropDisable?: boolean;
}

export const ClassContextBar: React.FC<ClassContextBarProps> = ({
  selectedClass,
  classes,
  onSelectClass,
  dropDisable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const printStatusMap: Record<string, string> = {
    printed: "Printing Completed",
    requested: "Requested",
    approved: "In Progress",
    rejected: "Rejected / Failed",
    not_yet_requested: "Not Started",
  };

  const printStatusStyles: Record<string, string> = {
    printed: "text-[#23A346] bg-[#E6F7ED] border-[#C8EED8]",
    requested: "text-[#af50bf] bg-[#f8e6fa] border-[#e6c2eb]",
    approved: "text-[#FFB800] bg-[#FFF6D9] border-[#FFE9A6]",
    rejected: "text-[#E53935] bg-[#FDE7E7] border-[#F5BCBC]",
    not_yet_requested: "text-[#FF7A1A] bg-[#FFF0E6] border-[#FFD3B3]",
    yet_started: "text-[#FF7A1A] bg-[#FFF0E6] border-[#FFD3B3]",
  };

  console.log(classes, "classes");
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (cls: ScheduledClass | "custom") => {
    onSelectClass?.(cls);
    setIsOpen(false);
  };

  const isCustom = selectedClass === "custom" || selectedClass === null;
  const currentClass = isCustom ? null : (selectedClass as ScheduledClass);

  return (
    <div
      className="bg-white border-b border-slate-200 px-6 py-4 mb-4 shadow-sm"
      ref={dropdownRef}
    >
      <label className="block text-[14px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
        Requesting Materials For:
      </label>

      <div className="relative">
        <button
          disabled={dropDisable}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-[100%] flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
            isOpen
              ? "ring-2 ring-brand-100 border-brand-300"
              : "border-slate-200 hover:border-brand-300 hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-3 overflow-hidden">
            {isCustom ? (
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-[34px] h-[34px]  rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                  <Layers className="w-[18px] h-[18px]" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Custom Request</div>
                  <div className="text-[12px] text-slate-500">
                    Not linked to a specific scheduled class
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-[34px] h-[34px] rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <Calendar className="w-[18px] h-[18px]" />
                </div>
                <div className="text-left">
                  <div className=" text-[14px] font-semibold flex items-center gap-3">
                    {currentClass?.yearLevel} - {currentClass?.subject}
                    <span
                      className={`text-[13px] font-[600] px-2 py-0.5 rounded-full border ${
                        printStatusStyles[
                          currentClass?.print_request_status ?? ""
                        ] || "text-slate-400 bg-slate-100 border-slate-200"
                      }`}
                    >
                      {printStatusMap[
                        currentClass?.print_request_status ?? ""
                      ] || "Unknown"}
                    </span>
                  </div>
                  <div className="text-[13px] text-slate-500 flex items-center gap-2">
                    <span>
                      {currentClass?.date} ({currentClass?.day})
                    </span>
                    <span>•</span>
                    <span>{currentClass?.center}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!dropDisable &&(
          <ChevronDown
            className={`h-5 text-slate-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && !dropDisable && (
          <div className="absolute top-full left-0 w-full mt-2 bg-white rounded-lg shadow-xl border border-slate-100 z-50 max-h-80 overflow-y-auto">
            {/* Custom Request Option */}
            <div
              onClick={() => !dropDisable && handleSelect("custom")}
              className={`p-3 cursor-pointer border-b border-slate-100 hover:bg-purple-50 transition-colors ${
                isCustom ? "bg-purple-50" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-[28px] h-[28px] rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                  <Layers className="w-[14px] h-[14px]" />
                </div>
                <div>
                  <div className=" text-[14px] font-medium text-slate-900">
                    Custom Request
                  </div>
                  <div className="text-[12px] text-slate-500">
                    Select materials without linking to a schedule
                  </div>
                </div>
              </div>
            </div>

            <div className="px-3 py-2 text-[12px] font-semibold text-slate-400 bg-slate-50 uppercase tracking-wider">
              Upcoming Classes
            </div>

            {classes.map((cls: any) => (
              <div
                key={cls.csms_id}
                className={`class-item ${
                  !isCustom && currentClass?.csms_id === cls.csms_id
                    ? "selected"
                    : ""
                }`}
                onClick={() => !dropDisable && handleSelect(cls)}
              >
                <div className="class-info">
                  <Calendar
                    style={{ width: 16, height: 16, color: "#64748b" }}
                  />
                  <span className="label">{cls.date}</span>
                </div>

                <div className="class-info">
                  <MapPin style={{ width: 16, height: 16, color: "#64748b" }} />
                  <span className="value">{cls.center}</span>
                </div>

                <div className="class-info">
                  <Book style={{ width: 16, height: 16, color: "#64748b" }} />
                  <span className="value">
                    {cls.yearLevel} - {cls.subject}
                  </span>
                </div>

                <div className="classstatus-box">
                  <span className={cls.print_request_status}>
                    {printStatusMap[cls.print_request_status] || "Unknown"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
